import { h } from 'preact';
import { useContext } from 'preact/compat';
import CustomAmount from './CustomAmount';
import { isSelected } from '../../lib/helpers';
import { Theme } from '../../lib/render';
import {
	CartActionType, useCart, formatCurrency,
} from '../../hooks/cart';
import type { Target } from '../../types';

/**
 * Candidate Button Group.
 * Renders a group of buttons for a candidate + cutsom input.
 *
 * @param props.target The candidate to render.
 * @param props.count Optional max number of btns to include in the group.
 * @param props.amounts Optional array of amounts to use for the btns.
 * @param props.followUpAction Optional action to run after a btn select is added to cart.
 * Defaults to theme base amounts.
 */
const AmountButtonGroup = ({
	target,
	count = 2,
	amounts = [],
	followUpAction = null,
}: {
	target: Target,
	count?: number,
	amounts?: number[]|false,
	followUpAction?: () => void,
}) => {
	const theme = useContext(Theme);
	const { cart, dispatch } = useCart();

	// Default to theme amounts if none are provided.
	const useableAmounts = Array.isArray(amounts) && amounts.length ? amounts : theme.amounts;

	return (
		<div className="ea-am-btn-group">
			{useableAmounts.slice(0, count).map((am) => {
				const selected = isSelected(cart.selections, am, target.id);
				return (
					<button
						className="ea-am-btn-group__btn"
						type="button"
						key={am}
						data-selected={selected}
						onClick={(e) => {
							e.preventDefault();
							dispatch({
								type: CartActionType.TOGGLE_SELECTION,
								payload: {
									...target,
									amount: am,
								},
							});
							/**
							 * Run the follow up action if we are adding to the cart.
							 * Use case: Upsell setState to final confirm.
							 */
							if (!selected) {
								followUpAction?.();
							}
						}}
					>
						<span>
							{formatCurrency(am)}
						</span>
					</button>
				);
			})}
			<CustomAmount
				target={target}
				className="ea-am-btn-group__custom-input"
				followUpAction={followUpAction}
			/>
		</div>
	);
};

export default AmountButtonGroup;
